import React, {Component, Fragment} from 'react'
import StyleSheet from 'styles'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import TextInput from '@material-ui/core/TextField'
import moment from 'moment'
import { InvestorActions, IssuerActions, CountryActions, UserActions, SnackbarActions } from 'actionsets'
import { connect } from 'react-redux'
import Dependent from 'containers/shared/Dependent'
import { ContactUs } from 'containers'
import CenteredContent from 'containers/CenteredContent'
import {Review} from 'components/account'
import {PasswordStrength, StyledForm, ConfirmationDialog, FormContext, CountrySelect, InputDialog} from 'components'
import {createMuiTheme, MuiThemeProvider} from '@material-ui/core/styles'
import {deepMerge} from 'utils/CollectionUtils'
import {darkThemeDefinition} from 'styles/AppStyles'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import {DialogHandler, Authorization} from 'utils'
import {Checkbox} from 'components/styled/Checkbox'
import {Enable2faOtp} from 'components/user/Enable2fa'
import {FormattedMessage} from 'react-intl'

export class Profile extends Component{

  constructor(props) {
    super(props)
    InvestorActions.bindActions(this, 'investor')
    IssuerActions.bindActions(this, 'issuer')
    CountryActions.bindActions(this, 'country')
    UserActions.bindActions(this, 'user')
    SnackbarActions.bindActions(this, 'snackbar')
    this.state = {
      context: {}
    }
    this.accountTypeDialogHandler = new DialogHandler(this, 'accountTypeDialog')
  }

  dependsOn = async() => {
    await this.actions.user.show({id: this.userId})

    const actions = []
    if (this.user.issuerStatus !== 'not_applied')
      actions.push(this.actions.issuer.show({id: this.user.id}))
    if (this.user.investorStatus !== 'not_applied')
      actions.push(this.actions.investor.show({id: this.user.id}))

    actions.push(this.actions.country.show({id: this.user.country.id}))
    actions.push(this.actions.country.index())

    await Promise.all(actions)
  }

  dependenciesMet = () => {
    return this.props.user.id && this.props.country.id &&
           (this.user.issuerStatus === 'not_applied' || this.props.issuer.id) &&
           (this.user.investorStatus === 'not_applied' || this.props.investor.id)
  }

  get userId() {
    return this.props.match.params.id || this.props.currentUser.id
  }

  get userAge(){
    return this.user ?
      'About ' + moment.duration(moment().diff(moment(this.user.createdAt))).humanize()+ ' ago' :
      ''
  }

  get user(){
    return this.props.user || {}
  }

  get userContext(){
    return {
      ...this.user,
      countryId: this.user.country.id,
      ...this.state.context,
    }
  }

  get errors() {
    return (this.props.errors.update || {}).meta
  }

  get account(){
    const account = (this.user.issuerStatus !== 'not_applied' && this.props.issuer) ||
                    (this.user.investorStatus !== 'not_applied' && this.props.investor)

    const {country, user} = this.props
    return account && {...account, country, user}
  }

  get accountType() {
    return this.account.type === 'issuerAccounts' ? 'issuer' : 'investor'
  }

  handleChangeAccountType = async() => {
    const result = await this.accountTypeDialogHandler.show({initialValue: this.accountType})
    if (result && result !== this.accountType) {
      try {
        await this.actions.user.changeAccountType({id: this.user.id, accountType: result})
        this.actions.snackbar.show("Account type updated")
      }
      catch (e) {
        this.actions.snackbar.error("Could not change the account type")
      }
    }

    await this.actions[result].show({id: this.user.id})
  }

  handleDelete = async() => {
    try {
      await this.actions.user.destroy({id: this.props.user.id})
      this.actions.snackbar.show('User deleted')
      this.props.history.push(`/users`)
    }
    catch (err) {
      this.actions.snackbar.error('Could not delete user')
    }
  }

  handleEditAccount = (accountType = this.accountType) => () => {
    this.props.history.push(`/${accountType}/${this.user.id}/edit`)
  }

  handleDialogOpen = (name) => () => {
    this.setState({[`${name}Open`]: true})
  }

  handleDialogSave = (name, saving) => async() => {
    if (!saving || await this.updateUser())
      this.setState({[`${name}Open`]: false, context: {}})
  }

  handleUserChange = (context) => {
    this.setState({context})
  }

  handle2fa = () => {
    const {otpRequiredForLogin} = this.userContext
    if (!otpRequiredForLogin) {
      this.generate2faSecret()
    }
    this.setState({
      context: {
        ...this.state.context,
        otpRequiredForLogin: !otpRequiredForLogin
      },
      [otpRequiredForLogin ? 'disable2faOpen' : 'enable2faOpen']: true
    })
  }

  handleReset2fa = () => {
    this.setState({
      context: {...this.state.context, otpRequiredForLogin: null}
    }, this.handleDialogSave('reset2fa', true))
  }

  generate2faSecret = async() => {
    const {data: {secret}} = await this.actions.user.generate2faSecret({id: this.props.user.id})
    this.setState({context: {...this.state.context, secret}})
  }

  updateUser = async() => {
    try {
      await this.actions.user.update(this.userContext)
      this.actions.snackbar.show("Saved")
      return true
    }
    catch (e) {
      this.actions.snackbar.error("Save Failed")
      return false
    }
  }

  renderDetailsPanel = () =>
    <div className={styles.panel}>
      <Typography variant='h5'>User Details</Typography>
      <div className={styles.section}>
        <TextInput label='Name' value={this.user.name} disabled/>
        <TextInput label='Email' value={this.user.email} disabled/>
        <TextInput label='Country' value={this.user.country.name} disabled/>
        <TextInput label='Signed Up' value={this.userAge} disabled/>
        {Authorization.user.id === this.props.user.id &&
          <Fragment>
            <Typography variant="body1">
              <Checkbox style={{marginLeft: -12}} onClick={this.handle2fa} checked={this.userContext.otpRequiredForLogin}/>
              <FormattedMessage id="profile.2fa.enabled_label" />
            </Typography>
            <Typography>
              <FormattedMessage id="profile.2fa.description" />
            </Typography>
          </Fragment>
        }
      </div>
      <div className={styles.buttonBox}>
        <Button variant='contained' color='secondary' onClick={this.handleDialogOpen('editDetails')}>
          <FormattedMessage id="profile.actions.edit" />
        </Button>
        {Authorization.user.id !== this.props.user.id &&
        <Fragment>
          <Button variant='contained' color='secondary' onClick={this.handleDialogOpen('delete')}>
            <FormattedMessage id="profile.actions.delete" />
          </Button>
          <Button variant="contained" color="primary" onClick={this.handleDialogOpen('reset2fa')}>
            <FormattedMessage id="profile.actions.reset_2fa" />
          </Button>
        </Fragment>
        }
        <Button variant='contained' color='primary' onClick={this.handleDialogOpen('password')}>
          <FormattedMessage id="profile.actions.change_password" />
        </Button>
      </div>
      <ContactUs.Link className={styles.contactUs}/>
    </div>

  renderAccountPanel = () =>
    <div className={styles.panel}>
      <Typography variant='h5'>
        <FormattedMessage id={`account.${this.accountType}.title`} />
      </Typography>
      <div className={styles.section} style={{paddingRight: 5}}>
        <Review context={this.account}/>
      </div>
      <div className={styles.buttonBox}>
        <Button variant='contained' color='secondary' onClick={this.handleEditAccount()}>
          <FormattedMessage id="account.actions.edit" />
        </Button>
        {Authorization.admin &&
          <Button variant='contained' color='primary' onClick={this.handleChangeAccountType}>
            <FormattedMessage id="account.actions.change_type" />
          </Button>
        }
      </div>
    </div>

  renderNoAccountPanel = () =>
    <div class={styles.panel}>
      <Typography variant='h5'>
        <FormattedMessage id="account.setup.title" />
      </Typography>
      <div className={styles.buttonBox}>
        <Button variant='contained' color='secondary' onClick={this.handleEditAccount('investor')}>
          <FormattedMessage id="account.setup.actions.investor" />
        </Button>
        {Authorization.admin &&
          <Button variant='contained' color='primary' onClick={this.handleEditAccount('issuer')}>
            <FormattedMessage id="account.setup.actions.issuer" />
          </Button>
        }
      </div>
    </div>

  renderConfirmDelete = () =>
    <ConfirmationDialog
      title={<FormattedMessage id="profile.actions.delete" />}
      open={!!this.state.deleteOpen}
      onConfirm={this.handleDelete}
      onCancel={this.handleDialogSave('delete', false)}
      confirmLabel={<FormattedMessage id="profile.actions.delete" />}
    >
      <Typography>
        <FormattedMessage id="profile.actions.delete.confirmation" />
      </Typography>
    </ConfirmationDialog>

  renderChangePassword = () =>
    <ConfirmationDialog
      title={<FormattedMessage id="profile.actions.change_password" />}
      open={!!this.state.passwordOpen}
      onConfirm={this.handleDialogSave('password', true)}
      onCancel={this.handleDialogSave('password', false)}
      confirmLabel={'Save'}
      canConfirm={!!this.userContext.password}
    >
      <MuiThemeProvider theme={formTheme}>
        <FormContext onChange={this.handleUserChange} context={this.userContext} errorContext={this.errors}>
          {Authorization.user.id === this.props.user.id &&
            <TextInput member="oldPassword" type="password"/>
          }
          <TextInput label="New Password" member="password" type="password"/>
          <PasswordStrength password={this.state.context.password} minScore={2}/>
          <TextInput label="Confirm New Password" member="passwordConfirmation" type="password"/>
        </FormContext>
      </MuiThemeProvider>
    </ConfirmationDialog>


  renderEditDetails = () =>
    <ConfirmationDialog
      title={<FormattedMessage id="profile.actions.edit" />}
      open={!!this.state.editDetailsOpen}
      onConfirm={this.handleDialogSave('editDetails', true)}
      onCancel={this.handleDialogSave('editDetails', false)}
      confirmLabel={'Save'}
    >
      <MuiThemeProvider theme={formTheme}>
        <FormContext onChange={this.handleUserChange} context={this.userContext} errorContext={this.errors}>
          <TextInput member='name'/>
          <TextInput member='email'/>
          <CountrySelect member='countryId' label='Country' countries={this.props.countries}/>
        </FormContext>
      </MuiThemeProvider>
    </ConfirmationDialog>

  renderReset2fa = () =>
    <ConfirmationDialog
      title="Reset 2FA"
      open={!!this.state.reset2faOpen}
      onConfirm={this.handleReset2fa}
      onCancel={this.handleDialogSave('reset2fa', false)}
      confirmLabel={'Reset'}
    >
      <Typography>
        <FormattedMessage id="profile.actions.reset_2fa.confirmation" />
      </Typography>
    </ConfirmationDialog>

  renderDisable2fa = () =>
    <ConfirmationDialog
      title="Disable 2FA"
      open={!!this.state.disable2faOpen}
      onConfirm={this.handleDialogSave('disable2fa', true)}
      onCancel={this.handleDialogSave('disable2fa', false)}
      confirmLabel={'OK'}
    >
      <Typography>
        <FormattedMessage id="profile.2fa.disable.confirmation" />
      </Typography>
    </ConfirmationDialog>

  renderEnable2fa = () =>
    <ConfirmationDialog
      title={<FormattedMessage id="profile.2fa.enable.title" />}
      open={!!this.state.enable2faOpen}
      onConfirm={this.handleDialogSave('enable2fa', true)}
      onCancel={this.handleDialogSave('enable2fa', false)}
      confirmLabel={'Save'}
    >
      <FormContext onChange={this.handleUserChange} context={this.userContext} errorContext={this.errors}>
        <Enable2faOtp member="otpAttempt" user={this.userContext} secret={this.userContext.secret}/>
      </FormContext>
    </ConfirmationDialog>


  render = () =>
    <CenteredContent>
      <div className={styles.content}>
        <StyledForm>
          {this.renderDetailsPanel()}
        </StyledForm>

        {this.user.accessLevel !== 'admin' &&
          <StyledForm>
            {this.account && this.renderAccountPanel()}
            {!this.account && this.renderNoAccountPanel()}
          </StyledForm>
        }
      </div>
      {this.renderConfirmDelete()}
      {this.renderChangePassword()}
      {this.renderEditDetails()}
      {this.renderEnable2fa()}
      {this.renderDisable2fa()}
      {this.renderReset2fa()}
      <InputDialog title={<FormattedMessage id="account.actions.change_type.title" />} confirmLabel="Change" {...this.accountTypeDialogHandler.props}>
        <RadioGroup name='registeredFor1' member='registeredFor' required doesNotDisplayErrors>
          <Typography>
            <FormattedMessage id="account.actions.change_type.message" />
          </Typography>
          <FormControlLabel value="investor" control={<Radio/>}
                            label={<FormattedMessage id="account.investor" />} />
          <FormControlLabel value="issuer" control={<Radio/>}
                            label={<FormattedMessage id="account.issuer" />} />
        </RadioGroup>
      </InputDialog>
    </CenteredContent>

}

const formTheme =
  createMuiTheme(deepMerge(darkThemeDefinition, {
    overrides: {
      MuiFormControl: {
        root: {
          marginTop: 10,
          marginBottom: 2,
        }
      },
    }
  }))


const styles = StyleSheet({
  '@media (max-width: 975px)':{
    card: {
      '&:nth-child(1)': {
        minWidth: '100%'
      }
    }
  },
  content: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: -40,
    '& > div': {
      flex: 1,
      margin: 40
    },
    '& h5': {
      borderBottom: '1px solid #1B3044'
    }
  },
  section: {
    marginBottom: 50,
    position: 'relative'
  },
  buttonBox: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: -10,
    marginBottom: 30,
    '& > button': {
      margin: [5, 10],
      flex: 1,
      width: 'auto',
      minWidth: 200,
    }
  },
  panel: {
    maxWidth: 556,
    minWidth: 450,
    '@media(max-width: 600px)': {
      minWidth: 300,
    }
  },
})
export default connect(({tokens, users, investors, issuers, countries}) => ({...investors, ...issuers, ...countries, ...tokens, ...users}))(Dependent({loader: true})(Profile))